import { useMemo } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import { selectActiveAccount, useAuthStore } from '@features/auth';
import { queryClient } from '@lib/queryClient';
import type { ApiError } from '@/types/api.types';

import { getBusinessHours, putBusinessHours } from '../api/businessHoursApi';
import type { ApiDayCode, BusinessHours } from '../types';

// ─── Types (mirrored from onboarding without creating a cross-feature dep) ────

type DayOfWeek =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export interface DaySchedule {
  open: boolean;
  from: string;
  to: string;
}

export type ScheduleData = Record<DayOfWeek, DaySchedule>;

// ─── Conversion helpers ───────────────────────────────────────────────────────

const API_TO_DAY: Record<ApiDayCode, DayOfWeek> = {
  MON: 'monday',
  TUE: 'tuesday',
  WED: 'wednesday',
  THU: 'thursday',
  FRI: 'friday',
  SAT: 'saturday',
  SUN: 'sunday',
};

const DAY_TO_API: Record<DayOfWeek, ApiDayCode> = {
  monday: 'MON',
  tuesday: 'TUE',
  wednesday: 'WED',
  thursday: 'THU',
  friday: 'FRI',
  saturday: 'SAT',
  sunday: 'SUN',
};

const DAYS_ORDER: DayOfWeek[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const DEFAULT_FALLBACK: ScheduleData = {
  monday: { open: true, from: '09:00', to: '17:00' },
  tuesday: { open: true, from: '09:00', to: '17:00' },
  wednesday: { open: true, from: '09:00', to: '17:00' },
  thursday: { open: true, from: '09:00', to: '17:00' },
  friday: { open: true, from: '09:00', to: '17:00' },
  saturday: { open: false, from: '09:00', to: '17:00' },
  sunday: { open: false, from: '09:00', to: '17:00' },
};

export function apiToSchedule(hours: BusinessHours): ScheduleData {
  const result: ScheduleData = { ...DEFAULT_FALLBACK };
  for (const entry of hours.schedule) {
    const day = API_TO_DAY[entry.day];
    if (day) {
      result[day] = {
        open: !entry.isClosed,
        from: entry.startTime ?? '09:00',
        to: entry.endTime ?? '17:00',
      };
    }
  }
  return result;
}

export function scheduleToApi(schedule: ScheduleData, timezone: string): BusinessHours {
  return {
    timezone,
    schedule: DAYS_ORDER.map((day) => {
      const s = schedule[day];
      return s.open
        ? { day: DAY_TO_API[day], isClosed: false, startTime: s.from, endTime: s.to }
        : { day: DAY_TO_API[day], isClosed: true, startTime: null, endTime: null };
    }),
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

const hoursKey = (accountId: string) => ['settings', 'hours', accountId] as const;

export function useBusinessHours() {
  const activeAccount = useAuthStore(selectActiveAccount);
  const accountId = activeAccount?.accountId ?? '';

  const query = useQuery({
    queryKey: hoursKey(accountId),
    queryFn: () => getBusinessHours(accountId),
    enabled: !!accountId,
  });

  const mutation = useMutation<BusinessHours, ApiError, BusinessHours>({
    mutationFn: (body) => putBusinessHours(accountId, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(hoursKey(accountId), updated);
    },
  });

  const schedule = useMemo(
    () => (query.data ? apiToSchedule(query.data) : undefined),
    [query.data],
  );

  return {
    hours: query.data,
    schedule,
    timezone: query.data?.timezone ?? 'UTC',
    isLoading: query.isLoading,
    fetchError: query.error as ApiError | null,
    update: mutation.mutate,
    isUpdating: mutation.isPending,
    updateError: mutation.error,
  };
}
